import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  Image,
  Input,
  Progress,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiCheck, FiChevronDown, FiImage, FiLayers, FiUpload, FiVideo, FiX } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import type { MarketplaceOrderDeliverable, MarketplaceSubmissionStatus, MarketplaceSubmissionUnit } from '@foodfluence/shared';
import { lcApi } from '../../../services/api/lc';
import { ff } from '../../../theme';
import { Pill } from '../../../components/ui';
import { uploadWithProgress } from '../../../utils/contentUploadUtils';
import { MarketplaceOrderStickyHeader } from '../components/MarketplaceOrderStickyHeader';
import { marketplaceOrderIsRawUgcRequirement } from '../utils/marketplaceOrderUploadCopy';
import {
  buildMarketplaceUploadSections,
  marketplaceUploadTotalSlots,
  type MarketplaceUploadSectionPostLink,
  type MarketplaceUploadSectionSingle,
} from '../utils/marketplaceOrderUploadSections';
import { formatDueDateLabel } from '../utils/marketplaceOrderUtils';

const CHECKLIST_ITEMS: string[] = [
  'Film in highest resolution your phone allows (4K or 1080p).',
  'Keep vertical 9:16 framing unless the brief says otherwise.',
  'Avoid heavy filters or watermarks unless the brand requested them.',
  'Upload edited finals here; add raw clips in the Raw section when listed.',
  'Check lighting and audio — hook viewers in the first 3 seconds.',
];

function formatFileMb(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (!Number.isFinite(mb)) return '—';
  return mb >= 100 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
}

/** Resolve icon from deliverable type */
function deliverableIcon(row?: MarketplaceOrderDeliverable | null): React.ElementType {
  if (!row) return FiVideo;
  if (marketplaceOrderIsRawUgcRequirement(row)) return FiLayers;
  if (row.type === 'instagram_story' || row.type === 'ugc_photo' || row.type === 'ugc_photo_carousel') return FiImage;
  return FiVideo;
}

function MediaThumb(props: {
  file?: File | null;
  emptyLabel?: string;
  onClick: () => void;
  onRemove?: () => void;
  boxSize?: string;
}) {
  const url = useMemo(() => (props.file ? URL.createObjectURL(props.file) : null), [props.file]);
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  const size = props.boxSize ?? '64px';

  return (
    <Box position="relative" w={size} h={size} flexShrink={0}>
      <Flex
        w="full"
        h="full"
        borderRadius="14px"
        bg={ff.grey}
        border="1px dashed"
        borderColor={ff.border}
        align="center"
        justify="center"
        overflow="hidden"
        cursor="pointer"
        onClick={props.onClick}
        role="group"
      >
        {url && props.file?.type.startsWith('image/') ? (
          <Image src={url} alt="" objectFit="cover" w="full" h="full" />
        ) : url && props.file?.type.startsWith('video/') ? (
          <Box as="video" src={url} muted playsInline w="full" h="full" objectFit="cover" />
        ) : (
          <Flex direction="column" align="center" gap={1} px={1}>
            <Icon as={FiUpload} color={ff.text2} boxSize={4} />
            <Text fontSize="10px" color={ff.text2} textAlign="center" lineHeight="shorter">
              {props.emptyLabel ?? 'Add'}
            </Text>
          </Flex>
        )}
      </Flex>
      {props.file && props.onRemove ? (
        <Flex
          position="absolute"
          top="6px"
          right="6px"
          w="24px"
          h="24px"
          borderRadius="full"
          bg={ff.ink}
          align="center"
          justify="center"
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation();
            props.onRemove?.();
          }}
          aria-label="Remove file"
        >
          <Icon as={FiX} color="white" boxSize={3} />
        </Flex>
      ) : null}
    </Box>
  );
}

export default function MarketplaceOrderUploadContentPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const checklist = useDisclosure({ defaultIsOpen: false });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<{
    id: string;
    brandID: string;
    brandName?: string;
    brandInstagramHandle?: string;
    cash: number;
    orderBrief?: unknown;
    deliverables?: Record<string, MarketplaceOrderDeliverable>;
    submissions?: Record<string, MarketplaceSubmissionUnit>;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pickRef = useRef<string | null>(null);

  const [files, setFiles] = useState<Record<string, File>>({});
  const [submitted, setSubmitted] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  const [lastSent, setLastSent] = useState<Record<string, File>>({});
  // Tracks server-side submission status for slots that were already submitted
  const [serverSubmissions, setServerSubmissions] = useState<Record<string, MarketplaceSubmissionStatus>>({});
  // Tracks revision feedback text from the brand, keyed by slotKey
  const [revisionFeedback, setRevisionFeedback] = useState<Record<string, string>>({});

  const [postLinkUrls, setPostLinkUrls] = useState<Record<string, string>>({});
  const [postLinkSubmitted, setPostLinkSubmitted] = useState<Record<string, boolean>>({});
  const [postLinkSubmitting, setPostLinkSubmitting] = useState<Record<string, boolean>>({});
  const [postLinkLastSent, setPostLinkLastSent] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!orderId) {
      setError('Missing order id.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    lcApi
      .getMarketplaceOrder(orderId)
      .then((res) => {
        if (cancelled) return;
        setOrder(res.data);

        // Pre-populate state from existing submissions
        const subs = res.data.submissions ?? {};
        const initialUrls: Record<string, string> = {};
        const initialPostSubmitted: Record<string, boolean> = {};
        const initialPostLastSent: Record<string, string> = {};
        const initialServerSubmissions: Record<string, MarketplaceSubmissionStatus> = {};
        const initialRevisionFeedback: Record<string, string> = {};
        for (const [slotKey, unit] of Object.entries(subs)) {
          if (unit.postUrl) {
            const sectionId = `postLink:${slotKey}`;
            initialUrls[sectionId] = unit.postUrl;
            initialPostLastSent[sectionId] = unit.postUrl;
            initialPostSubmitted[sectionId] = true;
          }
          // File slots: status badge when we have uploaded assets.
          // Post-link slots: no `assets` — use postUrl + status so "Approved" / review states show.
          const hasAssets = Boolean(unit.assets && unit.assets.length > 0);
          const hasPostLink = Boolean(unit.postUrl?.trim());
          if (
            unit.status &&
            unit.status !== 'pendingSubmission' &&
            (hasAssets || hasPostLink)
          ) {
            initialServerSubmissions[slotKey] = unit.status;
          }
          if (unit.status === 'revisionRequested' && unit.feedback) {
            initialRevisionFeedback[slotKey] = unit.feedback;
          }
        }
        if (Object.keys(initialUrls).length > 0) {
          setPostLinkUrls(initialUrls);
          setPostLinkLastSent(initialPostLastSent);
          setPostLinkSubmitted(initialPostSubmitted);
        }
        if (Object.keys(initialServerSubmissions).length > 0) {
          setServerSubmissions(initialServerSubmissions);
        }
        if (Object.keys(initialRevisionFeedback).length > 0) {
          setRevisionFeedback(initialRevisionFeedback);
        }
      })
      .catch((e) => {
        console.error('Error loading marketplace order', e);
        if (!cancelled) setError('Could not load order.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const deliverablesMap = order?.deliverables ?? {};
  const sections = useMemo(() => buildMarketplaceUploadSections(deliverablesMap), [deliverablesMap]);
  const totalSlots = useMemo(() => marketplaceUploadTotalSlots(sections), [sections]);

  const filledSlots = useMemo(() => {
    let n = 0;
    for (const s of sections) {
      if (s.kind === 'single') {
        if (files[s.sectionId] || serverSubmissions[s.slotKey]) n += 1;
      } else {
        if (postLinkUrls[s.sectionId]?.trim()) n += 1;
      }
    }
    return n;
  }, [sections, files, postLinkUrls, serverSubmissions]);

  const percent = totalSlots > 0 ? Math.min(100, Math.round((filledSlots / totalSlots) * 100)) : 0;

  const dueText = formatDueDateLabel(
    order?.orderBrief && typeof order.orderBrief === 'object' && order.orderBrief !== null && 'logistics' in order.orderBrief
      ? (order.orderBrief as { logistics?: { contentDeliveryTargetDate?: string | null } }).logistics?.contentDeliveryTargetDate ??
          null
      : null
  );

  const brandName = order?.brandName?.trim() || 'Brand';
  const avatarLetter = (brandName.trim()[0] ?? 'B').toUpperCase();

  const reqById = useCallback((id: string) => deliverablesMap[id], [deliverablesMap]);

  const openPicker = (sectionId: string) => {
    pickRef.current = sectionId;
    fileInputRef.current?.click();
  };

  const submitSingle = async (section: MarketplaceUploadSectionSingle) => {
    const file = files[section.sectionId];
    if (!file) {
      toast({ title: 'Add a file first', status: 'warning', duration: 2000 });
      return;
    }
    if (!orderId) return;

    setSubmitting((prev) => ({ ...prev, [section.sectionId]: true }));
    try {
      const { url, filePath } = await lcApi.generateContentUploadUrl(orderId, {
        slotId: section.slotKey,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });
      await uploadWithProgress(url, file, () => {});
      await lcApi.submitContent(orderId, {
        slotId: section.slotKey,
        files: [{ filePath, mimeType: file.type || undefined }],
      });
      setSubmitted((prev) => ({ ...prev, [section.sectionId]: true }));
      setLastSent((prev) => ({ ...prev, [section.sectionId]: file }));
      toast({ title: `${section.title} submitted`, status: 'success', duration: 2000 });
    } catch (e) {
      console.error('submitSingle error', e);
      toast({ title: 'Upload failed', description: 'Please try again.', status: 'error', duration: 3000 });
    } finally {
      setSubmitting((prev) => ({ ...prev, [section.sectionId]: false }));
    }
  };

  const submitPostLink = async (section: MarketplaceUploadSectionPostLink) => {
    const url = postLinkUrls[section.sectionId]?.trim();
    if (!url) {
      toast({ title: 'Paste the post URL first', status: 'warning', duration: 2000 });
      return;
    }
    if (!orderId) return;

    setPostLinkSubmitting((prev) => ({ ...prev, [section.sectionId]: true }));
    try {
      await lcApi.submitPostLink(orderId, { slotId: section.requirementId, postUrl: url });
      setPostLinkSubmitted((prev) => ({ ...prev, [section.sectionId]: true }));
      setPostLinkLastSent((prev) => ({ ...prev, [section.sectionId]: url }));
      toast({ title: `${section.title} link submitted`, status: 'success', duration: 2000 });
    } catch (e) {
      console.error('submitPostLink error', e);
      toast({ title: 'Submission failed', description: 'Please try again.', status: 'error', duration: 3000 });
    } finally {
      setPostLinkSubmitting((prev) => ({ ...prev, [section.sectionId]: false }));
    }
  };

  if (loading) {
    return (
      <Flex minH="100svh" align="center" justify="center" direction="column" gap={3} bg="white">
        <Spinner size="lg" color={ff.ink} thickness="3px" />
        <Text fontSize="14px" color={ff.text2}>
          Loading...
        </Text>
      </Flex>
    );
  }

  if (error || !order) {
    return (
      <Flex minH="100svh" align="center" justify="center" direction="column" gap={3} bg="white" px={6}>
        <Text textStyle="display" fontSize="22px" color={ff.ink}>
          Could not load
        </Text>
        <Text fontSize="14px" color={ff.text2} textAlign="center">
          {error ?? 'Order not found.'}
        </Text>
        <Button variant="inkOutline" h="48px" px={6} onClick={() => navigate(-1)}>
          Go back
        </Button>
      </Flex>
    );
  }

  return (
    <Box bg="white" minH="100svh">
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/*,video/*,.mov,.mp4,.m4v,.webm"
        onChange={(e) => {
          const sid = pickRef.current;
          pickRef.current = null;
          const picked = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (!sid || picked.length === 0) return;
          setFiles((prev) => ({ ...prev, [sid]: picked[0] }));
        }}
      />

      <MarketplaceOrderStickyHeader
        onBack={() => navigate(-1)}
        avatarLetter={avatarLetter}
        brandName={brandName}
        instagramHandle={order?.brandInstagramHandle}
        trailing={
          <Box textAlign="right">
            <Pill tone="ink">
              <Icon as={FiUpload} boxSize={3} mr="6px" />
              Submitting
            </Pill>
            <Text mt={1} fontSize="12px" fontWeight="500" color={ff.ink}>
              {dueText ?? ''}
            </Text>
          </Box>
        }
      >
        <Flex mt={3} align="center" justify="space-between">
          <Text fontSize="12px" fontWeight="500" color={ff.ink} lineHeight="18px">
            {filledSlots} of {totalSlots} uploaded
          </Text>
          <Text fontSize="12px" fontWeight="500" color={ff.ink} lineHeight="18px">
            {percent}%
          </Text>
        </Flex>
        <Box mt={2} px={1}>
          <Progress
            value={percent}
            height="6px"
            borderRadius="9999px"
            bg={ff.grey}
            sx={{ '& > div': { background: ff.ink } }}
          />
        </Box>
      </MarketplaceOrderStickyHeader>

      <Box px={4} pt={4} pb={10}>
        {/* Checklist card */}
        <Box
          border="1px solid"
          borderColor={ff.line}
          borderRadius="20px"
          bg="white"
          mb={4}
          overflow="hidden"
        >
          <Flex
            as="button"
            type="button"
            w="100%"
            align="center"
            gap={3}
            px={4}
            py={4}
            onClick={checklist.onToggle}
            textAlign="left"
          >
            <Flex w="36px" h="36px" borderRadius="12px" bg={ff.limeSurface} align="center" justify="center" flexShrink={0}>
              <Text fontSize="16px" color={ff.ink} fontWeight="600" lineHeight={1}>
                ✓
              </Text>
            </Flex>
            <Box flex={1} minW={0}>
              <Text textStyle="eyebrow" color={ff.text2}>
                Checklist
              </Text>
              <Text fontSize="15px" fontWeight="600" color={ff.ink} mt={0.5}>
                Before you submit
              </Text>
            </Box>
            <Flex align="center" gap={2} flexShrink={0}>
              <Text fontSize="13px" fontWeight="500" color={ff.text2}>
                {CHECKLIST_ITEMS.length}
              </Text>
              <Icon
                as={FiChevronDown}
                color={ff.ink}
                boxSize={4}
                transform={checklist.isOpen ? 'rotate(180deg)' : undefined}
                transition="transform 0.2s ease"
              />
            </Flex>
          </Flex>
          <Collapse in={checklist.isOpen} animateOpacity>
            <Box px={4} pb={4} pt={0} borderTop="1px solid" borderTopColor={ff.line}>
              <Box as="ul" pl={5} m={0}>
                {CHECKLIST_ITEMS.map((item) => (
                  <Box as="li" key={item} fontSize="13px" color={ff.body} lineHeight="22px" mb={2}>
                    {item}
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* Deliverable cards */}
        <Flex direction="column" gap={4}>
          {sections.length === 0 ? (
            <Text fontSize="15px" color={ff.text2} px={1}>
              No deliverables to upload on this order.
            </Text>
          ) : null}
          {sections.map((section) => {
            const req = reqById(section.requirementId);
            const IconCmp = deliverableIcon(req);

            if (section.kind === 'postLink') {
              const postUrl = postLinkUrls[section.sectionId] ?? '';
              const submitted = postLinkSubmitted[section.sectionId];
              const submitting = postLinkSubmitting[section.sectionId];
              const hasPostLinkChanges = postUrl.trim() !== (postLinkLastSent[section.sectionId] ?? '');
              const canSubmit = postUrl.trim().length > 0 && !submitting && (!submitted || hasPostLinkChanges);
              const serverStatus = serverSubmissions[section.slotKey];
              const isPostApproved = serverStatus === 'approved';
              const postFeedback = revisionFeedback[section.slotKey];

              return (
                <Box
                  key={section.sectionId}
                  border="1px solid"
                  borderColor={ff.line}
                  borderRadius="20px"
                  bg={isPostApproved ? ff.limeSurface : 'white'}
                  overflow="hidden"
                >
                  <Flex px={4} pt={4} pb={3} align="flex-start" gap={3}>
                    <Flex w="32px" h="32px" borderRadius="12px" bg={ff.grey} align="center" justify="center" flexShrink={0}>
                      <Icon as={IconCmp} color={ff.ink} boxSize="17px" />
                    </Flex>
                    <Box flex={1} minW={0}>
                      <Text fontSize="15px" fontWeight="600" color={ff.ink} letterSpacing="-0.15px">
                        {section.title}
                      </Text>
                      <Text fontSize="12.5px" color={ff.text2} mt={0.5} lineHeight="18px">
                        {section.subtitle}
                      </Text>
                    </Box>
                    {serverStatus ? (
                      <Pill tone={serverStatus === 'approved' ? 'lime' : 'grey'}>
                        {serverStatus === 'approved' && <Icon as={FiCheck} boxSize={3} mr="4px" />}
                          {serverStatus === 'approved'
                            ? 'Approved'
                            : serverStatus === 'pendingPostReview'
                            ? 'In review'
                            : serverStatus === 'pendingReview'
                            ? 'In review'
                            : serverStatus === 'revisionRequested'
                            ? 'Revision'
                            : serverStatus}
                      </Pill>
                    ) : null}
                  </Flex>
                  <Box borderTop="1px solid" borderTopColor={ff.line} px={4} py={3}>
                    {postFeedback ? (
                      <Box
                        bg={ff.cream}
                        border="1px solid"
                        borderColor={ff.line}
                        borderRadius="14px"
                        px={3}
                        py={2.5}
                        mb={3}
                      >
                        <Text textStyle="eyebrow" color={ff.danger} mb={1}>
                          Revision requested
                        </Text>
                        <Text fontSize="13px" color={ff.body} lineHeight="1.5">
                          {postFeedback}
                        </Text>
                      </Box>
                    ) : null}
                    <Text fontSize="12px" fontWeight="500" color={ff.body} mb={2}>
                      Post live, then paste the link below
                    </Text>
                    <Flex gap={2}>
                      <Input
                        flex={1}
                        placeholder="https://www.instagram.com/p/..."
                        value={postUrl}
                        onChange={(e) =>
                          setPostLinkUrls((prev) => ({ ...prev, [section.sectionId]: e.target.value }))
                        }
                      />
                      <Button
                        variant="lime"
                        h="48px"
                        px={5}
                        fontSize="14px"
                        isDisabled={!canSubmit}
                        isLoading={submitting}
                        onClick={() => submitPostLink(section)}
                      >
                        {submitted ? 'Resubmit' : 'Submit'}
                      </Button>
                    </Flex>
                  </Box>
                </Box>
              );
            }

            const file = files[section.sectionId];
            const serverStatus = serverSubmissions[section.slotKey];
            const isSectionSubmitted = submitted[section.sectionId] || !!serverStatus;
            const isSectionSubmitting = submitting[section.sectionId];
            const hasFileChanged = file !== lastSent[section.sectionId];
            const canSubmit = Boolean(file) && !isSectionSubmitting && (!isSectionSubmitted || hasFileChanged);
            const isApproved = serverStatus === 'approved' && !file;
            const slotFeedback = revisionFeedback[section.slotKey];

            return (
              <Box
                key={section.sectionId}
                border="1px solid"
                borderColor={ff.line}
                borderRadius="20px"
                bg={isApproved ? ff.limeSurface : 'white'}
                overflow="hidden"
              >
                <Flex px={4} pt={4} pb={3} align="flex-start" gap={3}>
                  <Flex w="32px" h="32px" borderRadius="12px" bg={ff.grey} align="center" justify="center" flexShrink={0}>
                    <Icon as={IconCmp} color={ff.ink} boxSize="17px" />
                  </Flex>
                  <Box flex={1} minW={0}>
                    <Text fontSize="15px" fontWeight="600" color={ff.ink} letterSpacing="-0.15px">
                      {section.title}
                    </Text>
                    <Text fontSize="12.5px" color={ff.text2} mt={0.5} lineHeight="18px">
                      {section.subtitle}
                    </Text>
                  </Box>
                  {serverStatus && !file && (
                    <Pill tone={serverStatus === 'approved' ? 'lime' : 'grey'}>
                      {serverStatus === 'approved' && <Icon as={FiCheck} boxSize={3} mr="4px" />}
                        {serverStatus === 'approved'
                          ? 'Approved'
                          : serverStatus === 'pendingReview'
                          ? 'In review'
                          : serverStatus === 'revisionRequested'
                          ? 'Revision'
                          : serverStatus === 'draftApproved'
                          ? 'Draft OK'
                          : serverStatus}
                    </Pill>
                  )}
                </Flex>
                <Box borderTop="1px solid" borderTopColor={ff.line} px={4} py={3}>
                  {slotFeedback ? (
                    <Box
                      bg={ff.cream}
                      border="1px solid"
                      borderColor={ff.line}
                      borderRadius="14px"
                      px={3}
                      py={2.5}
                      mb={3}
                    >
                      <Text textStyle="eyebrow" color={ff.danger} mb={1}>
                        Revision requested
                      </Text>
                      <Text fontSize="13px" color={ff.body} lineHeight="1.5">
                        {slotFeedback}
                      </Text>
                    </Box>
                  ) : null}
                  <Flex align="flex-end" justify="space-between" gap={3}>
                    <Box flex={1} minW={0}>
                      <MediaThumb
                        file={file}
                        onClick={() => openPicker(section.sectionId)}
                        onRemove={
                          file
                            ? () => setFiles((prev) => { const n = { ...prev }; delete n[section.sectionId]; return n; })
                            : undefined
                        }
                      />
                      <Text fontSize="12px" color={ff.text2} mt={2}>
                        {file ? formatFileMb(file.size) : isApproved ? 'Previously submitted' : 'No file yet'}
                      </Text>
                    </Box>
                    <Button
                      variant="lime"
                      h="44px"
                      px={5}
                      fontSize="14px"
                      isDisabled={!canSubmit}
                      isLoading={isSectionSubmitting}
                      onClick={() => submitSingle(section)}
                    >
                      {isSectionSubmitted ? 'Resubmit' : 'Submit'}
                    </Button>
                  </Flex>
                </Box>
              </Box>
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
}
